/**
 * Provider Config Dialog
 * Dialog for adding or editing authentication provider configuration
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthProviderForm, ProviderTemplates } from '../types';
import { ProviderFormFields } from './ProviderFormFields';

interface ProviderConfigDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  form: AuthProviderForm;
  onChange: (key: string, value: string | boolean) => void;
  onChangeProviderType: (type: string) => void;
  onSave: () => void;
  loading: boolean;
  error: string | null;
  templates: ProviderTemplates;
}

/**
 * Dialog for configuring authentication providers (add/edit)
 */
export const ProviderConfigDialog: React.FC<ProviderConfigDialogProps> = ({
  open,
  onClose,
  mode,
  form,
  onChange,
  onChangeProviderType,
  onSave,
  loading,
  error,
  templates,
}) => {
  const { t } = useTranslation();

  const currentTemplate = templates[form.provider_type];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'edit'
          ? t('auth.providers.editProvider')
          : t('auth.providers.addProvider')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('auth.providers.fields.providerType')}</InputLabel>
          <Select
            value={form.provider_type}
            label={t('auth.providers.fields.providerType')}
            onChange={(e) => onChangeProviderType(e.target.value)}
            disabled={mode === 'edit'}
          >
            {Object.entries(templates).map(([key, template]) => (
              <MenuItem key={key} value={key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {template.icon}
                  <Box>
                    <Typography variant="body1">{template.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {template.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {currentTemplate && (
          <ProviderFormFields
            providerType={form.provider_type}
            config={form.config}
            onChange={onChange}
            template={currentTemplate}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('auth.providers.cancel')}
        </Button>
        <Button onClick={onSave} variant="contained" disabled={loading}>
          {loading ? t('auth.providers.saving') : t('auth.providers.saveProvider')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
